// services/aiService.js — JobEng AI Service Layer v4 (SECURE PROXY EDITION)
// ALL AI calls now go through the JobEng server proxy at /api/ai (Groq).
// 🔐 NO provider API keys live in this app any more. The previous version
//    shipped Anthropic/OpenAI/OpenRouter keys inside the public app binary —
//    those keys must be considered compromised and revoked.
// All exports remain backwards-compatible with every screen in the app:
// dedup · retry backoff · usage stats · caching · JSON repair all preserved.

import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';

// ─────────────────────────────────────────────────────────────────────────────
// 🌐 JOBENG AI PROXY
// ─────────────────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getProxyHeaders = async () => {
  let tok = null;
  try {
    tok = (await AsyncStorage.getItem('auth_token')) ||
          (await AsyncStorage.getItem('userToken')) ||
          (await AsyncStorage.getItem('token'));
  } catch { /* ignore */ }
  return {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
};

// Provider descriptors kept for backwards compatibility with screens that
// display provider info (getActiveProvider / setPreferredProvider etc.)
const AI_PROVIDERS = {
  JOBENG: {
    name: 'JobEng AI', shortName: 'JobEng',
    models: { default: 'jobeng-ai', fast: 'jobeng-ai', smart: 'jobeng-ai', letter: 'jobeng-ai', code: 'jobeng-ai' },
    priority: 1, type: 'proxy',
  },
  FALLBACK: {
    name: 'Offline Mode', shortName: 'Offline',
    models: { default: 'mock', fast: 'mock', smart: 'mock', letter: 'mock', code: 'mock' },
    priority: 2, type: 'mock',
  },
};

// Task types → preserved for compatibility (the proxy chooses the model)
const TASK_MODEL_MAP = {
  ideas: 'default', business_plan: 'smart', analysis: 'default',
  chat: 'default', letter: 'letter', email: 'fast', document: 'default',
  risk: 'default', cv: 'default', crm: 'fast', erp: 'fast',
  ml: 'default', marketing: 'fast', translation: 'fast', summary: 'fast',
  code: 'code', sentiment: 'fast', search: 'fast', report: 'default',
};

// ─────────────────────────────────────────────────────────────────────────────
// 🗂 MODULE STATE
// ─────────────────────────────────────────────────────────────────────────────
let activeProvider    = 'JOBENG';
let providerStatus    = { JOBENG: true };
let lastProviderCheck = 0;
const PROVIDER_CHECK_TTL = 5 * 60 * 1000;
const pendingRequests = {}; // request deduplication
let usageStats        = { calls: 0, tokens: 0, byProvider: {}, byFunction: {}, errors: 0, cacheHits: 0 };

// ─────────────────────────────────────────────────────────────────────────────
// 🛡️ SAFE HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const safeArray  = (arr)           => Array.isArray(arr)              ? arr  : [];
const safeObject = (obj, def = {}) => (obj && typeof obj === 'object') ? obj  : def;
const safeString = (str, def = '') => (str && typeof str === 'string') ? str  : def;
const safeNumber = (n,   def = 0)  => { const v = parseInt(n);   return isNaN(v) ? def : v; };
const safeFloat  = (n,   def = 0)  => { const v = parseFloat(n); return isNaN(v) ? def : v; };

const trackUsage = (fnName, provider, tokensEst = 0) => {
  usageStats.calls++;
  usageStats.tokens += tokensEst;
  usageStats.byProvider[provider] = (usageStats.byProvider[provider] || 0) + 1;
  usageStats.byFunction[fnName]   = (usageStats.byFunction[fnName]   || 0) + 1;
};

// ─────────────────────────────────────────────────────────────────────────────
// 🧹 JSON CLEANER + REPAIR  (handles truncated / cut-off AI responses)
// ─────────────────────────────────────────────────────────────────────────────
const cleanJSON = (raw) => {
  if (!raw) return '{}';
  let s = raw.trim()
    .replace(/^```json\s*/i, '').replace(/^```\s*/, '').replace(/```\s*$/, '')
    .replace(/,(\s*[}\]])/g, '$1')
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, ' ');
  const m = s.match(/(\[[\s\S]*\]|\{[\s\S]*\})/);
  return m ? m[0] : s;
};

const repairJSON = (raw) => {
  if (!raw) return null;
  try { return JSON.parse(cleanJSON(raw)); } catch { /* continue */ }

  let s = raw.trim()
    .replace(/^```json\s*/i, '').replace(/^```\s*/, '').replace(/```\s*$/, '')
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, ' ').trimEnd();

  s = s.replace(/,\s*"[^"]*"\s*:\s*"?[^"}\\\]]*$/, '');
  s = s.replace(/,\s*$/, '');

  const opens = { '{': 0, '[': 0 };
  let inStr = false, escape = false;
  for (const ch of s) {
    if (escape)                  { escape = false; continue; }
    if (ch === '\\' && inStr)    { escape = true;  continue; }
    if (ch === '"')              { inStr = !inStr;  continue; }
    if (inStr)                    continue;
    if (ch === '{' || ch === '[') opens[ch]++;
    if (ch === '}')               opens['{'] = Math.max(0, opens['{'] - 1);
    if (ch === ']')               opens['['] = Math.max(0, opens['['] - 1);
  }

  const repaired = s + '}'.repeat(opens['{']) + ']'.repeat(opens['[']);
  try { return JSON.parse(repaired); } catch { return null; }
};

const extractPartialArray = (raw) => {
  if (!raw) return [];
  const cleaned = raw
    .replace(/^```json\s*/i, '').replace(/^```\s*/, '')
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, ' ').trim();

  const start = cleaned.indexOf('[');
  if (start === -1) return [];

  const results = [];
  let depth = 0, inStr = false, escape = false, objStart = -1;

  for (let i = start; i < cleaned.length; i++) {
    const ch = cleaned[i];
    if (escape)               { escape = false; continue; }
    if (ch === '\\' && inStr) { escape = true;  continue; }
    if (ch === '"')           { inStr = !inStr;  continue; }
    if (inStr)                 continue;
    if (ch === '{')           { if (depth === 1) objStart = i; depth++; }
    else if (ch === '}')      { depth--; if (depth === 1 && objStart !== -1) { try { results.push(JSON.parse(cleaned.slice(objStart, i + 1))); } catch {} objStart = -1; } }
    else if (ch === '[')       depth++;
    else if (ch === ']')       depth--;
  }
  return results;
};

// ─────────────────────────────────────────────────────────────────────────────
// 💾 RESPONSE CACHE (AsyncStorage, configurable TTL)
// ─────────────────────────────────────────────────────────────────────────────
const CACHE_TTL  = 10 * 60 * 1000;
const CACHE_PFX  = 'ai_cache_v3_';

const cacheGet = async (key, ttl = CACHE_TTL) => {
  try {
    const raw = await AsyncStorage.getItem(CACHE_PFX + key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > ttl) { AsyncStorage.removeItem(CACHE_PFX + key); return null; }
    usageStats.cacheHits++;
    return data;
  } catch { return null; }
};

const cacheSet = async (key, data) => {
  try { await AsyncStorage.setItem(CACHE_PFX + key, JSON.stringify({ data, ts: Date.now() })); }
  catch { /* non-critical */ }
};

export const clearAICache = async (tag = null) => {
  try {
    const all  = await AsyncStorage.getAllKeys();
    const keys = all.filter(k => k.startsWith(CACHE_PFX) && (tag ? k.includes(tag) : true));
    await AsyncStorage.multiRemove(keys);
    return { cleared: keys.length };
  } catch { return { cleared: 0 }; }
};

// ─────────────────────────────────────────────────────────────────────────────
// 🔄 PROVIDER SELECTION & HEALTH (now a single proxy health check)
// ─────────────────────────────────────────────────────────────────────────────
const selectProvider = async () => {
  try {
    const net = await NetInfo.fetch();
    if (!net.isConnected) { activeProvider = 'FALLBACK'; return 'FALLBACK'; }
  } catch { /* assume online */ }

  const now = Date.now();
  if (now - lastProviderCheck < PROVIDER_CHECK_TTL) {
    activeProvider = providerStatus.JOBENG ? 'JOBENG' : 'FALLBACK';
    return activeProvider;
  }

  try {
    const ctrl = new AbortController();
    const tid  = setTimeout(() => ctrl.abort(), 4000);
    const r = await fetch(`${API_BASE}/health`, { signal: ctrl.signal });
    clearTimeout(tid);
    providerStatus.JOBENG = r.ok;
  } catch { providerStatus.JOBENG = false; }

  lastProviderCheck = now;
  activeProvider = providerStatus.JOBENG ? 'JOBENG' : 'FALLBACK';
  return activeProvider;
};

export const getActiveProvider = () => ({
  provider: activeProvider,
  name:     AI_PROVIDERS[activeProvider]?.name      || 'Unknown',
  shortName:AI_PROVIDERS[activeProvider]?.shortName || '?',
  model:    AI_PROVIDERS[activeProvider]?.models?.default || 'unknown',
  status:   { ...providerStatus },
});

export const setPreferredProvider = (key) => {
  // Kept for backwards compatibility — all traffic goes through the JobEng proxy.
  if (AI_PROVIDERS[key]) { activeProvider = key; lastProviderCheck = 0; }
};

export const checkProviderHealth = async () => {
  lastProviderCheck = 0;
  await selectProvider();
  return { ...providerStatus };
};

// ─────────────────────────────────────────────────────────────────────────────
// 🚀 UNIFIED CHAT COMPLETION via JobEng proxy
//     (dedup, retry backoff, timeout, usage tracking all preserved)
// ─────────────────────────────────────────────────────────────────────────────
const flattenMessages = (messages) => {
  // The proxy accepts a single prompt string; merge system + history clearly.
  const sys  = messages.find(m => m.role === 'system');
  const rest = messages.filter(m => m.role !== 'system');
  const parts = [];
  if (sys) parts.push(`Instructions: ${sys.content}`);
  if (rest.length === 1) {
    parts.push(String(rest[0].content ?? ''));
  } else {
    rest.forEach(m => parts.push(`${m.role === 'assistant' ? 'Assistant' : 'User'}: ${m.content}`));
    parts.push('Assistant:');
  }
  return parts.join('\n\n');
};

const chatCompletion = async (messages, options = {}, preferred = null) => {
  const dedupKey = JSON.stringify({ messages, options });
  if (pendingRequests[dedupKey]) return pendingRequests[dedupKey];

  const execute = async () => {
    const prompt = flattenMessages(messages);
    const headers = await getProxyHeaders();
    const maxRetries = 2;
    let lastError;

    for (let retry = 0; retry <= maxRetries; retry++) {
      if (retry > 0) await new Promise(r => setTimeout(r, 500 * Math.pow(2, retry)));
      try {
        const ctrl = new AbortController();
        const tid  = setTimeout(() => ctrl.abort(), options.timeout || 60_000);

        const res = await fetch(`${API_BASE}/api/ai`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            prompt,
            max_tokens: options.max_tokens ?? 2000,
            temperature: options.temperature ?? 0.7,
            task: options.task || 'default',
          }),
          signal: ctrl.signal,
        });
        clearTimeout(tid);

        if (!res.ok) {
          const e = await res.text().catch(() => '');
          throw new Error(`JobEng AI HTTP ${res.status}: ${e.slice(0, 200)}`);
        }

        const data = await res.json();
        const text = data?.content || data?.message || data?.text || '';
        if (!text) throw new Error('Empty response from AI proxy');

        activeProvider = 'JOBENG';
        providerStatus.JOBENG = true;
        trackUsage(options.fnName || 'unknown', 'JOBENG', options.max_tokens || 500);

        return {
          choices: [{ message: { content: text, role: 'assistant' } }],
          _provider: 'JOBENG',
          _model: 'jobeng-ai',
        };
      } catch (err) {
        console.warn(`⚠️ JobEng AI attempt ${retry + 1}: ${err.message}`);
        lastError = err;
        if (err.name === 'AbortError' && retry === maxRetries) break;
      }
    }

    providerStatus.JOBENG = false;
    usageStats.errors++;
    throw lastError || new Error('AI service unavailable');
  };

  pendingRequests[dedupKey] = execute().finally(() => delete pendingRequests[dedupKey]);
  return pendingRequests[dedupKey];
};

export const singleCompletion = async (prompt, opts = {}, preferred = null) => {
  const data = await chatCompletion([{ role: 'user', content: prompt }], opts, preferred);
  return data.choices[0].message.content || '';
};

// ─────────────────────────────────────────────────────────────────────────────
// 💬 CONVERSATION HISTORY PERSISTENCE
// ─────────────────────────────────────────────────────────────────────────────
export const saveConversationHistory = async (contextId, history) => {
  try {
    await AsyncStorage.setItem(`ai_convo_${contextId}`, JSON.stringify({ history, savedAt: new Date().toISOString() }));
  } catch { /* non-critical */ }
};

export const loadConversationHistory = async (contextId) => {
  try {
    const raw = await AsyncStorage.getItem(`ai_convo_${contextId}`);
    if (!raw) return [];
    return JSON.parse(raw).history || [];
  } catch { return []; }
};

export const clearConversationHistory = async (contextId) => {
  try { await AsyncStorage.removeItem(`ai_convo_${contextId}`); } catch {}
};

// ─────────────────────────────────────────────────────────────────────────────
// 📊 USAGE STATS
// ─────────────────────────────────────────────────────────────────────────────
export const getUsageStats = () => ({ ...usageStats });

export const resetUsageStats = () => {
  usageStats = { calls: 0, tokens: 0, byProvider: {}, byFunction: {}, errors: 0, cacheHits: 0 };
};

// ─────────────────────────────────────────────────────────────────────────────
// 💡 GENERATE AI IDEAS
// ─────────────────────────────────────────────────────────────────────────────
const TOKENS_PER_IDEA = 350;
const MAX_IDEA_TOKENS = 1200;

const normaliseIdea = (idea, index, provKey, modelName, industry, reqId) => ({
  id:            `${(provKey || 'ai').toLowerCase()}-${reqId}-${index}`,
  title:         safeString(idea.title || idea.name,       `${industry} Innovation ${index + 1}`),
  description:   safeString(idea.description || idea.desc, 'Innovative engineering solution.'),
  type:          safeString(idea.type,                      'ai_ml').toLowerCase(),
  stage:         'idea',
  impact:        safeString(idea.impact,                    'high').toLowerCase(),
  estimated_roi: safeNumber(idea.estimated_roi,             250000),
  budget:        safeNumber(idea.budget, Math.round(safeNumber(idea.estimated_roi, 250000) * 0.3)),
  tags:          Array.isArray(idea.tags) ? idea.tags : [industry, 'Innovation', 'AI'],
  confidence:    safeNumber(idea.confidence,                85),
  timeline:      safeString(idea.timeline,                  '3 months'),
  industry:      safeString(idea.industry,                  industry),
  ml_generated:  true, ai_mode: provKey, provider: provKey, model: modelName || 'unknown',
  created_at:    new Date().toISOString(),
  owner:         { name: AI_PROVIDERS[provKey]?.name || 'AI', id: 'ai' },
  progress: 0, votes: 0, notes: [], bookmarked: false,
});

const buildIdeasPrompt = (existing, requestCount, industry) => ({
  system:
    `You are an elite AI innovation architect specialising in UK engineering sectors. ` +
    `Generate NOVEL, DISRUPTIVE, and PRACTICAL innovation ideas. ` +
    `ALWAYS respond with a valid complete JSON array. No markdown, no preamble. ` +
    `Keep each description to ONE sentence only.`,
  user:
    `Portfolio:\n${existing}\n\nGenerate EXACTLY ${requestCount} innovation idea${requestCount > 1 ? 's' : ''} for the ${industry} sector.\n\n` +
    `Each: title (≤8 words), description (1 sentence), ` +
    `type (ai_ml|automation|analytics|blockchain|vr_ar|product|process|technology), ` +
    `impact (low|medium|high|critical), estimated_roi (int 150000-2000000), ` +
    `budget (int ~30% of roi), tags (array of 3), confidence (int 70-98), timeline ("X months"), industry ("${industry}").\n\n` +
    `Respond ONLY with a complete JSON array.`,
});

export const generateAIIdeas = async (innovations, count = 3, industry = 'Software', preferred = null) => {
  const reqId    = Date.now();
  const cacheKey = `ideas_${industry}_${count}`;
  try {
    const cached = await cacheGet(cacheKey);
    if (cached) return cached;

    const safeInns = safeArray(innovations);
    const existing = safeInns.length
      ? safeInns.slice(0, 4).map(i => `- ${safeString(i?.title, 'Untitled')} (${safeString(i?.type, 'unknown')})`).join('\n')
      : 'None yet';
    const maxTok = Math.min(MAX_IDEA_TOKENS, count * TOKENS_PER_IDEA + 100);

    let rawContent = '', provKey = '', modelName = '';
    try {
      const { system, user } = buildIdeasPrompt(existing, count, industry);
      const data = await chatCompletion(
        [{ role: 'system', content: system }, { role: 'user', content: user }],
        { temperature: 0.85, max_tokens: maxTok, task: 'ideas', fnName: 'generateAIIdeas' }, preferred
      );
      rawContent = data.choices[0].message.content || '';
      provKey = data._provider || activeProvider; modelName = data._model || 'unknown';
    } catch (e) { console.warn(`⚠️ [${reqId}] Batch idea call failed: ${e.message}`); }

    let rawIdeas = [];
    if (rawContent) {
      const repaired = repairJSON(rawContent);
      if (repaired) {
        if      (Array.isArray(repaired))             rawIdeas = repaired;
        else if (Array.isArray(repaired.innovations)) rawIdeas = repaired.innovations;
        else if (Array.isArray(repaired.ideas))       rawIdeas = repaired.ideas;
        else if (Array.isArray(repaired.data))        rawIdeas = repaired.data;
        else if (repaired.title || repaired.name)     rawIdeas = [repaired];
      }
      if (!rawIdeas.length) rawIdeas = extractPartialArray(rawContent);
    }

    if (rawIdeas.length < count) {
      for (let i = rawIdeas.length; i < count; i++) {
        try {
          const { system, user } = buildIdeasPrompt(existing, 1, industry);
          const data = await chatCompletion(
            [{ role: 'system', content: system }, { role: 'user', content: user }],
            { temperature: 0.85 + i * 0.02, max_tokens: TOKENS_PER_IDEA + 80, task: 'ideas', fnName: 'generateAIIdeas' }, preferred
          );
          const single = repairJSON(data.choices[0].message.content || '');
          if (!provKey) { provKey = data._provider || activeProvider; modelName = data._model || 'unknown'; }
          if (single) {
            if (Array.isArray(single) && single[0]) rawIdeas.push(single[0]);
            else if (single.title || single.name)   rawIdeas.push(single);
          }
        } catch (e) { console.warn(`⚠️ Single idea #${i} failed: ${e.message}`); }
      }
    }

    if (!rawIdeas.length) throw new Error('No ideas parsed');
    const result = rawIdeas.slice(0, count).map((idea, i) => normaliseIdea(idea, i, provKey, modelName, industry, reqId));
    if (result.length < count) result.push(...getMockIdeas(count - result.length, industry, reqId));
    await cacheSet(cacheKey, result);
    return result;

  } catch (err) {
    console.error(`❌ generateAIIdeas: ${err.message}`);
    return getMockIdeas(count, industry, reqId);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 📊 GENERATE BUSINESS PLAN
// ─────────────────────────────────────────────────────────────────────────────
export const generateBusinessPlan = async (companyName, innovations, metrics, preferred = null) => {
  const reqId = Date.now();
  try {
    const safeInns = safeArray(innovations); const safeMet = safeObject(metrics);
    const portfolio = safeInns.length
      ? safeInns.slice(0, 6).map(i => `- ${safeString(i?.title, 'Untitled')}: ${safeString(i?.description, '').slice(0, 80)} (Stage: ${i?.stage || 'idea'}, ROI: £${safeNumber(i?.estimated_roi).toLocaleString()})`).join('\n')
      : 'New venture — no existing products';

    const data = await chatCompletion([
      { role: 'system', content: `You are a world-class UK business consultant. Create investor-ready business plans. Return ONLY valid JSON. No markdown.` },
      { role: 'user',   content:
          `Business plan for: ${safeString(companyName, 'an AI engineering company')} (UK)\n\nPORTFOLIO:\n${portfolio}\n\n` +
          `METRICS: active_experiments:${safeMet.active_experiments||12}, ai_accuracy:${safeMet.ai_accuracy||92}%, roi:${safeMet.roi||156}%, pipeline:£${safeNumber(safeMet.pipeline_value, 1250000).toLocaleString()}\n\n` +
          `Return JSON: { "executive_summary":"", "market_analysis":"", "products_services":"", "marketing_strategy":"", "financial_projections":"", "risk_assessment":"", "team_structure":"", "funding_requirements":500000 }` },
    ], { temperature: 0.7, max_tokens: 2000, task: 'business_plan', fnName: 'generateBusinessPlan' }, preferred);

    const parsed = repairJSON(data.choices[0].message.content);
    if (!parsed) throw new Error('Parse failed');
    return { ...parsed, funding_requirements: safeNumber(parsed.funding_requirements, 500000), provider: data._provider || activeProvider, generated_at: new Date().toISOString() };

  } catch (err) {
    console.error(`❌ generateBusinessPlan: ${err.message}`);
    return getMockBusinessPlan(companyName, innovations, metrics);
  }
};

export const generateTemplateBusinessPlan = (companyName, innovations, metrics) =>
  getMockBusinessPlan(companyName, innovations, metrics);

// ─────────────────────────────────────────────────────────────────────────────
// 💬 AI CHAT ADVISOR  (with streaming simulation + conversation history)
// ─────────────────────────────────────────────────────────────────────────────
export const getAIAdvice = async (
  userMessage, innovations, metrics, conversationHistory = [], streamCallback = null, preferred = null
) => {
  const reqId = Date.now();
  const safeInns = safeArray(innovations); const safeMet = safeObject(metrics); const safeHist = safeArray(conversationHistory);

  const ctx = {
    total: safeInns.length, active: safeInns.filter(i => !['completed', 'archived'].includes(i?.stage)).length,
    total_roi: safeInns.reduce((s, i) => s + safeNumber(i?.estimated_roi), 0),
    avg_roi: safeInns.length ? Math.round(safeInns.reduce((s, i) => s + safeNumber(i?.estimated_roi), 0) / safeInns.length) : 250000,
    top_projects: safeInns.slice(0, 3).map(i => safeString(i?.title, 'Unknown')),
    avg_health: safeInns.length ? Math.round(safeInns.reduce((s, i) => s + safeNumber(i?.health), 0) / safeInns.length) : 75,
    pipeline_value: safeNumber(safeMet.pipeline_value, 1250000),
  };

  const messages = [
    { role: 'system', content: `You are an AI Innovation Advisor for UK engineering companies. Be specific, actionable, reference projects by name. Keep responses to 2–3 paragraphs. Portfolio: ${JSON.stringify(ctx)}` },
    ...safeHist.slice(-6).filter(m => m.role && m.content),
    { role: 'user', content: safeString(userMessage, '') },
  ];

  try {
    const data    = await chatCompletion(messages, { temperature: 0.7, max_tokens: 1000, task: 'chat', fnName: 'getAIAdvice' }, preferred);
    const content = data.choices[0].message.content;

    if (streamCallback && typeof streamCallback === 'function') {
      const words = content.split(' '); let acc = '';
      for (const w of words) {
        acc += (acc ? ' ' : '') + w;
        streamCallback(acc);
        await new Promise(r => setTimeout(r, 18));
      }
    }
    return { message: content, provider: data._provider || activeProvider, model: data._model, timestamp: new Date().toISOString() };

  } catch (err) {
    console.error(`❌ getAIAdvice: ${err.message}`);
    const fallback = getDefaultResponse(userMessage, safeInns);
    if (streamCallback) streamCallback(fallback);
    return { message: fallback, provider: 'fallback', timestamp: new Date().toISOString() };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 🔍 ANALYSE INNOVATION
// ─────────────────────────────────────────────────────────────────────────────
export const analyzeInnovation = async (innovation, preferred = null) => {
  const reqId = Date.now(); const safeInn = safeObject(innovation);
  const cacheKey = `analysis_${safeString(safeInn.id, reqId.toString())}`;
  try {
    const cached = await cacheGet(cacheKey);
    if (cached) return cached;

    const data = await chatCompletion([
      { role: 'system', content: `You are a senior UK engineering innovation analyst. Return ONLY valid JSON. No markdown.` },
      { role: 'user',   content:
          `Analyse UK engineering innovation:\n` +
          `Title: ${safeString(safeInn.title, 'Untitled')}\nDesc: ${safeString(safeInn.description, '').slice(0, 300)}\n` +
          `Type: ${safeString(safeInn.type, 'unknown')}\nIndustry: ${safeString(safeInn.industry, 'Engineering')}\n` +
          `Impact: ${safeString(safeInn.impact, 'medium')}\nStage: ${safeString(safeInn.stage, 'idea')}\n` +
          `Progress: ${safeNumber(safeInn.progress)}%\nROI: £${safeNumber(safeInn.estimated_roi, 250000).toLocaleString()}\n` +
          `Budget: £${safeNumber(safeInn.budget, 80000).toLocaleString()}\nConfidence: ${safeNumber(safeInn.confidence, 85)}%\n\n` +
          `Return JSON: { success_probability:0-100, strengths:[3], risks:[3], recommendations:[3], next_steps:[3], ` +
          `market_fit_score:0-100, technical_readiness:0-100, time_to_market:"", competitive_advantage:"", summary:"one sentence" }` },
    ], { temperature: 0.5, max_tokens: 800, task: 'analysis', fnName: 'analyzeInnovation' }, preferred);

    const parsed = repairJSON(data.choices[0].message.content);
    if (!parsed) throw new Error('Parse failed');
    const enriched = { ...getDefaultAnalysis(innovation), ...parsed, provider: data._provider || activeProvider, analysed_at: new Date().toISOString() };
    await cacheSet(cacheKey, enriched);
    return enriched;

  } catch (err) {
    console.error(`❌ analyzeInnovation: ${err.message}`);
    return getDefaultAnalysis(innovation);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 🔍 COMPETITOR ANALYSIS
// ─────────────────────────────────────────────────────────────────────────────
export const analyzeCompetitors = async (innovation, preferred = null) => {
  const reqId = Date.now(); const safeInn = safeObject(innovation);
  const cacheKey = `competitors_${safeString(safeInn.id, reqId.toString())}`;
  try {
    const cached = await cacheGet(cacheKey);
    if (cached) return cached;

    const data = await chatCompletion([
      { role: 'user', content:
          `Competitive analysis for UK engineering innovation:\n"${safeString(safeInn.title, 'Innovation')}" — ${safeString(safeInn.description, '').slice(0, 200)}\nIndustry: ${safeString(safeInn.industry, 'Engineering')}\nType: ${safeString(safeInn.type, 'technology')}\n\n` +
          `Return JSON: { "competitors":[{"name":"","description":"","market_share":"","weakness":""}], "our_advantages":["","",""], "market_gap":"", "differentiation_score":0-100, "threat_level":"low|medium|high", "market_size":"", "growth_rate":"" }` },
    ], { temperature: 0.6, max_tokens: 700, task: 'analysis', fnName: 'analyzeCompetitors' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    if (!result) throw new Error('Parse failed');
    await cacheSet(cacheKey, result);
    return result;

  } catch (err) {
    console.error(`❌ analyzeCompetitors: ${err.message}`);
    return getDefaultCompetitorAnalysis(innovation);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 🔎 AI-POWERED SEARCH RANKING
// ─────────────────────────────────────────────────────────────────────────────
export const aiSearchInnovations = async (query, innovations, preferred = null) => {
  const safeInns = safeArray(innovations);
  if (!query.trim() || !safeInns.length) return safeInns;
  try {
    const titles = safeInns.map((i, idx) => `${idx}: ${safeString(i.title, 'Untitled')} — ${safeString(i.description, '').slice(0, 60)}`).join('\n');
    const data = await chatCompletion([
      { role: 'user', content: `List:\n${titles}\n\nQuery: "${query}"\n\nReturn JSON array of matching 0-based indices, most relevant first. [] if none. Example: [2, 0]` },
    ], { temperature: 0.1, max_tokens: 80, task: 'search', fnName: 'aiSearchInnovations' }, preferred);

    const parsed = repairJSON(data.choices[0].message.content || '[]');
    const indices = Array.isArray(parsed) ? parsed : [];
    if (indices.length) return indices.filter(i => typeof i === 'number' && i >= 0 && i < safeInns.length).map(i => safeInns[i]);
    return safeInns;
  } catch {
    const q = query.toLowerCase();
    return safeInns.filter(i => i.title?.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q) || (i.tags || []).some(t => t?.toLowerCase().includes(q)));
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 💰 ROI PROJECTION (5-year)
// ─────────────────────────────────────────────────────────────────────────────
export const generateROIProjection = async (innovation, years = 5, preferred = null) => {
  const safeInn = safeObject(innovation);
  try {
    const data = await chatCompletion([
      { role: 'user', content:
          `${years}-year financial ROI projection for UK engineering innovation:\n` +
          `Title: ${safeString(safeInn.title, 'Innovation')}\nIndustry: ${safeString(safeInn.industry, 'Engineering')}\n` +
          `Investment: £${safeNumber(safeInn.budget, 80000).toLocaleString()}\nExpected ROI: £${safeNumber(safeInn.estimated_roi, 250000).toLocaleString()}\nStage: ${safeString(safeInn.stage, 'idea')}\n\n` +
          `Return JSON: { "yearly":[{"year":1,"revenue":0,"costs":0,"profit":0,"cumulative_roi":0}], "payback_months":18, "irr_percent":35.2, "npv":180000, "assumptions":["","",""] }` },
    ], { temperature: 0.4, max_tokens: 700, fnName: 'generateROIProjection' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    if (!result) throw new Error('Parse failed');
    return result;
  } catch { return getDefaultROIProjection(innovation, years); }
};

// ─────────────────────────────────────────────────────────────────────────────
// 📋 PORTFOLIO HEALTH SUMMARY
// ─────────────────────────────────────────────────────────────────────────────
export const generatePortfolioSummary = async (innovations, metrics, preferred = null) => {
  const safeInns = safeArray(innovations); const safeMet = safeObject(metrics);
  try {
    const snapshot = {
      total: safeInns.length, total_roi: safeInns.reduce((s, i) => s + safeNumber(i?.estimated_roi), 0),
      avg_health: safeInns.length ? Math.round(safeInns.reduce((s, i) => s + safeNumber(i?.health), 0) / safeInns.length) : 0,
      by_stage: ['idea', 'research', 'build', 'pilot', 'scale', 'completed'].reduce((acc, s) => ({ ...acc, [s]: safeInns.filter(i => i.stage === s).length }), {}),
    };
    const data = await chatCompletion([
      { role: 'user', content:
          `Portfolio analysis: ${JSON.stringify(snapshot)}\nMetrics: ${JSON.stringify(safeMet)}\n\n` +
          `Return JSON: { "headline":"one line", "portfolio_score":0-100, "key_strengths":["",""], "urgent_actions":["",""], "opportunities":["",""], "outlook":"positive|neutral|concerning", "recommended_priority":"" }` },
    ], { temperature: 0.5, max_tokens: 600, fnName: 'generatePortfolioSummary' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    if (!result) throw new Error('Parse failed');
    return result;
  } catch { return getDefaultPortfolioSummary(innovations); }
};

// ─────────────────────────────────────────────────────────────────────────────
// ✉️ LETTER CONTENT GENERATOR  (for LettersManagementScreen)
// ─────────────────────────────────────────────────────────────────────────────
export const generateLetterContent = async (opts = {}, preferred = null) => {
  const { letterType = 'business', subject = '', recipientName = '', recipientCompany = '', prompt = '', tone = 'professional', context = '' } = opts;
  try {
    const data = await chatCompletion([
      { role: 'system', content: `You are an expert UK business letter writer. Write formal, professional letters in correct UK English. Return ONLY the letter body text — no salutation, no closing. 3-5 paragraphs.` },
      { role: 'user',   content:
          `Write a ${letterType} letter body for:\nSubject: ${subject || prompt}\nRecipient: ${recipientName}${recipientCompany ? `, ${recipientCompany}` : ''}\nTone: ${tone}\nContext: ${context || prompt}\n\n` +
          `UK formal letter body only. 3-5 paragraphs. No salutation, no closing, no signature.` },
    ], { temperature: 0.7, max_tokens: 800, task: 'letter', fnName: 'generateLetterContent' }, preferred);

    return data.choices[0].message.content || getMockLetterBody(letterType, subject, recipientName);
  } catch (err) {
    console.error(`❌ generateLetterContent: ${err.message}`);
    return getMockLetterBody(letterType, subject, recipientName);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 📧 EMAIL CONTENT GENERATOR  (for EmailManagementScreen)
// ─────────────────────────────────────────────────────────────────────────────
export const generateEmailContent = async (opts = {}, preferred = null) => {
  const { emailType = 'business', subject = '', recipientName = '', prompt = '', tone = 'professional', previousThread = '' } = opts;
  try {
    const data = await chatCompletion([
      { role: 'system', content: `You are an expert business email writer. Write concise, professional UK English emails. Return ONLY the email body text — no subject line, no greeting, no sign-off.` },
      { role: 'user',   content:
          `Write a ${emailType} email body:\nSubject: ${subject}\nRecipient: ${recipientName}\nTone: ${tone}\nContext: ${prompt}\n${previousThread ? `\nThread context:\n${previousThread.slice(0, 400)}` : ''}\n\n` +
          `Return just the email body. 2-4 paragraphs. Professional, concise, UK English.` },
    ], { temperature: 0.7, max_tokens: 600, task: 'email', fnName: 'generateEmailContent' }, preferred);

    return data.choices[0].message.content || getMockEmailBody(emailType, subject);
  } catch (err) {
    console.error(`❌ generateEmailContent: ${err.message}`);
    return getMockEmailBody(emailType, subject);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 📋 EMAIL TEMPLATE GENERATOR  (for EmailTemplatesScreen)
// ─────────────────────────────────────────────────────────────────────────────
export const generateEmailTemplate = async (opts = {}, preferred = null) => {
  const { name = 'Business Template', category = 'business', prompt = '', purpose = '' } = opts;
  const cacheKey = `email_tpl_${name.replace(/\s/g, '_').toLowerCase()}`;
  try {
    const cached = await cacheGet(cacheKey, 30 * 60 * 1000);
    if (cached) return cached;

    const data = await chatCompletion([
      { role: 'user', content:
          `Create a reusable email template for: "${name}" (${category})\nPurpose: ${purpose || prompt}\n\n` +
          `Return JSON: { "subject":"Subject line with [Variables]", "body":"Email body with [Variables] for customisation", "variables":["var1","var2"], "notes":"usage tips" }` },
    ], { temperature: 0.7, max_tokens: 700, task: 'email', fnName: 'generateEmailTemplate' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    if (!result) throw new Error('Parse failed');
    await cacheSet(cacheKey, result);
    return result;
  } catch (err) {
    console.error(`❌ generateEmailTemplate: ${err.message}`);
    return { subject: `[Template] ${name}`, body: `Dear [Recipient Name],\n\nI am writing regarding [Subject].\n\n[Main Content]\n\nPlease do not hesitate to contact us with any questions.\n\nKind regards,\n[Your Name]`, variables: ['Recipient Name', 'Subject', 'Main Content', 'Your Name'], notes: 'Replace variables in brackets before sending.' };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 📄 DOCUMENT SUMMARISER  (for DocumentsControlScreen)
// ─────────────────────────────────────────────────────────────────────────────
export const summariseDocument = async (documentText, docName = '', docType = '', preferred = null) => {
  try {
    const trimmed = documentText.slice(0, 3000);
    const data = await chatCompletion([
      { role: 'system', content: `You are a professional document analyst. Provide clear, structured summaries. Return ONLY valid JSON.` },
      { role: 'user',   content:
          `Summarise this ${docType || 'document'}${docName ? ` "${docName}"` : ''}:\n\n${trimmed}\n\n` +
          `Return JSON: { "summary":"2-3 sentence overview", "key_points":["","",""], "action_items":["",""], "document_type":"", "risk_level":"low|medium|high", "next_review_recommendation":"" }` },
    ], { temperature: 0.3, max_tokens: 600, task: 'summary', fnName: 'summariseDocument' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    return result || { summary: `${docName || 'Document'} reviewed and summarised.`, key_points: ['See original document for full details.'], action_items: [], document_type: docType, risk_level: 'low' };
  } catch (err) {
    console.error(`❌ summariseDocument: ${err.message}`);
    return { summary: `Unable to auto-summarise "${docName}". Please review manually.`, key_points: [], action_items: [], document_type: docType, risk_level: 'low' };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 🔤 GENERIC TEXT SUMMARISER
// ─────────────────────────────────────────────────────────────────────────────
export const summariseText = async (text, maxLength = 200, preferred = null) => {
  try {
    const data = await chatCompletion([
      { role: 'user', content: `Summarise in ${maxLength} characters or fewer:\n\n${text.slice(0, 2000)}\n\nReturn ONLY the summary text, nothing else.` },
    ], { temperature: 0.3, max_tokens: 300, task: 'summary', fnName: 'summariseText' }, preferred);
    return data.choices[0].message.content || text.slice(0, maxLength);
  } catch { return text.slice(0, maxLength); }
};

// ─────────────────────────────────────────────────────────────────────────────
// ⚠️ RISK ASSESSMENT GENERATOR  (for ProjectPlanningScreen & DocumentsControlScreen)
// ─────────────────────────────────────────────────────────────────────────────
export const generateRiskAssessment = async (projectName, projectDesc, sector = 'Engineering', preferred = null) => {
  const cacheKey = `risk_${projectName.replace(/\s/g, '_').toLowerCase()}`;
  try {
    const cached = await cacheGet(cacheKey, 15 * 60 * 1000);
    if (cached) return cached;

    const data = await chatCompletion([
      { role: 'user', content:
          `Risk assessment for UK ${sector} project:\nName: ${projectName}\nDesc: ${projectDesc.slice(0, 300)}\n\n` +
          `Return JSON: { "risks":[{"id":1,"category":"Technical|Financial|Operational|Legal|Environmental","title":"","description":"","likelihood":"Low|Medium|High","impact":"Low|Medium|High","risk_score":1-25,"mitigation":"","owner":"","status":"Open"}], "overall_risk_level":"Low|Medium|High|Critical", "key_concerns":["","",""], "regulatory_considerations":["",""] }` },
    ], { temperature: 0.5, max_tokens: 1000, task: 'risk', fnName: 'generateRiskAssessment' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    if (!result) throw new Error('Parse failed');
    await cacheSet(cacheKey, result);
    return result;
  } catch (err) {
    console.error(`❌ generateRiskAssessment: ${err.message}`);
    return getDefaultRiskAssessment(projectName);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 📅 MEETING AGENDA GENERATOR
// ─────────────────────────────────────────────────────────────────────────────
export const generateMeetingAgenda = async (opts = {}, preferred = null) => {
  const { title = 'Team Meeting', attendees = [], purpose = '', duration = 60, context = '' } = opts;
  try {
    const data = await chatCompletion([
      { role: 'user', content:
          `Create a professional meeting agenda:\nTitle: ${title}\nDuration: ${duration} minutes\nPurpose: ${purpose || context}\nAttendees: ${attendees.join(', ') || 'Team members'}\n\n` +
          `Return JSON: { "agenda_items":[{"order":1,"title":"","duration_mins":5,"presenter":"","type":"discussion|presentation|decision|info","notes":""}], "pre_meeting_actions":["",""], "objectives":["",""], "success_criteria":"" }` },
    ], { temperature: 0.6, max_tokens: 800, task: 'chat', fnName: 'generateMeetingAgenda' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    if (!result) throw new Error('Parse failed');
    return result;
  } catch (err) {
    console.error(`❌ generateMeetingAgenda: ${err.message}`);
    return { agenda_items: [{ order: 1, title: 'Welcome & Introductions', duration_mins: 5, presenter: 'Chair', type: 'info', notes: '' }, { order: 2, title: purpose || 'Main Discussion', duration_mins: Math.max(10, duration - 15), presenter: 'All', type: 'discussion', notes: '' }, { order: 3, title: 'Actions & Next Steps', duration_mins: 10, presenter: 'Chair', type: 'decision', notes: '' }], pre_meeting_actions: ['Review any pre-reading materials'], objectives: [purpose || 'Discuss key topics and agree next steps'], success_criteria: 'Clear action items assigned with owners and deadlines' };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 💼 JOB DESCRIPTION GENERATOR
// ─────────────────────────────────────────────────────────────────────────────
export const generateJobDescription = async (opts = {}, preferred = null) => {
  const { title = 'Role', sector = 'Engineering', salary = '', location = 'UK', requirements = [], preferred: pref } = opts;
  const cacheKey = `jd_${title.replace(/\s/g, '_').toLowerCase()}_${sector.toLowerCase()}`;
  try {
    const cached = await cacheGet(cacheKey, 60 * 60 * 1000);
    if (cached) return cached;

    const data = await chatCompletion([
      { role: 'system', content: `You are a UK HR specialist. Write compelling job descriptions that attract top talent. Return ONLY valid JSON.` },
      { role: 'user',   content:
          `UK job description:\nTitle: ${title}\nSector: ${sector}\nSalary: ${salary || 'Competitive'}\nLocation: ${location}\nKey requirements: ${requirements.join(', ') || 'Standard UK requirements'}\n\n` +
          `Return JSON: { "overview":"", "key_responsibilities":["","","","",""], "essential_requirements":["","","",""], "desirable_requirements":["","",""], "what_we_offer":["","",""], "company_overview":"", "application_process":"", "equal_opportunities_statement":"" }` },
    ], { temperature: 0.7, max_tokens: 1000, task: 'chat', fnName: 'generateJobDescription' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    if (!result) throw new Error('Parse failed');
    await cacheSet(cacheKey, result);
    return result;
  } catch (err) {
    console.error(`❌ generateJobDescription: ${err.message}`);
    return { overview: `Exciting opportunity for a ${title} in the ${sector} sector.`, key_responsibilities: [`Lead ${sector} projects`, 'Work with cross-functional teams', 'Deliver to deadlines and budget', 'Report to senior management', 'Continuously improve processes'], essential_requirements: [`${sector} experience`, 'Strong communication skills', 'UK right to work', 'Degree or equivalent'], desirable_requirements: ['Relevant professional qualification', 'UK industry experience', 'Management experience'], what_we_offer: ['Competitive salary', '25 days holiday', 'Pension scheme', 'Professional development'], company_overview: 'A leading UK engineering firm.', application_process: 'Submit CV and cover letter.', equal_opportunities_statement: 'We are an equal opportunities employer.' };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 📄 COVER LETTER GENERATOR
// ─────────────────────────────────────────────────────────────────────────────
export const generateCoverLetter = async (opts = {}, preferred = null) => {
  const { applicantName = '', jobTitle = '', company = '', keySkills = [], experience = '', jobRequirements = '' } = opts;
  try {
    const data = await chatCompletion([
      { role: 'system', content: `You are a UK career specialist. Write compelling, genuine cover letters. UK English, formal tone. Return ONLY the cover letter body text.` },
      { role: 'user',   content:
          `Cover letter body for:\nApplicant: ${applicantName}\nRole: ${jobTitle} at ${company}\nKey skills: ${keySkills.join(', ')}\nExperience: ${experience.slice(0, 300)}\nJob requires: ${jobRequirements.slice(0, 300)}\n\n` +
          `Write 3-4 professional paragraphs. Highlight relevant skills and enthusiasm. UK English. Body only (no Dear/Yours).` },
    ], { temperature: 0.7, max_tokens: 700, task: 'letter', fnName: 'generateCoverLetter' }, preferred);

    return data.choices[0].message.content || `I am writing to apply for the position of ${jobTitle} at ${company}. With my background in ${keySkills.slice(0, 2).join(' and ')}, I am confident I can make a significant contribution to your team.\n\nThroughout my career I have developed strong skills in ${experience.slice(0, 100)}. I am particularly drawn to this role because of ${company}'s reputation for excellence.\n\nI would welcome the opportunity to discuss my application further.`;
  } catch (err) {
    console.error(`❌ generateCoverLetter: ${err.message}`);
    return `I am writing to apply for the position of ${jobTitle} at ${company}.\n\nI bring strong relevant experience and am committed to delivering excellent results.\n\nI look forward to the opportunity to discuss my application.`;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 👤 CV ANALYSIS  (for recruitment/HR screens)
// ─────────────────────────────────────────────────────────────────────────────
export const analyzeCV = async (cvText, jobDescription = '', preferred = null) => {
  try {
    const data = await chatCompletion([
      { role: 'user', content:
          `Analyse this CV${jobDescription ? ' against the job description' : ''}:\n\nCV:\n${cvText.slice(0, 2000)}\n\n${jobDescription ? `Job Description:\n${jobDescription.slice(0, 1000)}` : ''}\n\n` +
          `Return JSON: { "match_score":0-100, "strengths":["","",""], "gaps":["",""], "key_skills":["","","","",""], "experience_years":0, "education_level":"", "recommendations":["","",""], "interview_questions":["","",""], "overall_assessment":"" }` },
    ], { temperature: 0.4, max_tokens: 800, task: 'cv', fnName: 'analyzeCV' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    return result || { match_score: 70, strengths: ['Relevant experience', 'Good qualifications', 'Clear career progression'], gaps: ['Further details needed'], key_skills: [], experience_years: 3, education_level: 'Degree', recommendations: ['Request references', 'Interview for cultural fit'], interview_questions: ['Tell me about your most challenging project?', 'What are your salary expectations?', 'Why are you interested in this role?'], overall_assessment: 'Candidate appears suitable. Recommend interview.' };
  } catch (err) {
    console.error(`❌ analyzeCV: ${err.message}`);
    return { match_score: 65, strengths: ['Review manually for full assessment'], gaps: ['AI analysis unavailable'], key_skills: [], experience_years: 0, education_level: 'Unknown', recommendations: ['Manual review required'], interview_questions: [], overall_assessment: 'Please review CV manually.' };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 💼 CRM INSIGHTS GENERATOR
// ─────────────────────────────────────────────────────────────────────────────
export const generateCRMInsights = async (clientData, interactions = [], deals = [], preferred = null) => {
  const safeClient = safeObject(clientData);
  const cacheKey = `crm_${safeString(safeClient.id || safeClient.name, 'unknown').replace(/\s/g, '_')}`;
  try {
    const cached = await cacheGet(cacheKey, 5 * 60 * 1000);
    if (cached) return cached;

    const data = await chatCompletion([
      { role: 'user', content:
          `CRM insights for UK engineering client:\nClient: ${safeString(safeClient.name, 'Client')}\nSector: ${safeString(safeClient.sector, 'Engineering')}\nValue: £${safeNumber(safeClient.value, 0).toLocaleString()}\nStatus: ${safeString(safeClient.status, 'Active')}\nInteractions: ${safeArray(interactions).length}\nDeals: ${safeArray(deals).length}\n\n` +
          `Return JSON: { "relationship_score":0-100, "risk_level":"low|medium|high", "opportunities":["","",""], "recommended_actions":["","",""], "next_contact_reason":"", "upsell_potential":"low|medium|high", "sentiment":"positive|neutral|negative", "key_insights":["",""] }` },
    ], { temperature: 0.6, max_tokens: 500, task: 'crm', fnName: 'generateCRMInsights' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    if (!result) throw new Error('Parse failed');
    await cacheSet(cacheKey, result);
    return result;
  } catch (err) {
    console.error(`❌ generateCRMInsights: ${err.message}`);
    return { relationship_score: 70, risk_level: 'low', opportunities: ['Explore additional service lines', 'Request referral to sister companies', 'Propose annual framework agreement'], recommended_actions: ['Schedule quarterly review meeting', 'Send case study of similar client success', 'Follow up on pending proposal'], next_contact_reason: 'Quarterly account review', upsell_potential: 'medium', sentiment: 'positive', key_insights: ['Active client with consistent engagement', 'Good potential for expansion'] };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 🛒 SALES SCRIPT GENERATOR
// ─────────────────────────────────────────────────────────────────────────────
export const generateSalesScript = async (opts = {}, preferred = null) => {
  const { product = '', client = '', painPoints = [], callType = 'cold', context = '' } = opts;
  try {
    const data = await chatCompletion([
      { role: 'user', content:
          `UK B2B sales script:\nProduct/Service: ${product}\nClient: ${client}\nCall type: ${callType}\nPain points: ${painPoints.join(', ')}\nContext: ${context}\n\n` +
          `Return JSON: { "opening":"", "value_proposition":"", "discovery_questions":["","",""], "objection_handlers":{"price":"","timing":"","competitor":"","not_interested":""}, "closing_techniques":["",""], "follow_up_plan":"", "key_messages":["","",""] }` },
    ], { temperature: 0.7, max_tokens: 800, task: 'crm', fnName: 'generateSalesScript' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    return result || { opening: `Hi [Name], I'm calling from JobEng about ${product}. Is now a good time?`, value_proposition: `We help engineering firms like yours with ${product}, typically delivering 30% efficiency improvements.`, discovery_questions: ['What are your biggest challenges with this currently?', 'What would solving this be worth to you?', 'What does your decision-making process look like?'], objection_handlers: { price: 'I understand. Can I ask what budget you were working with?', timing: 'What would need to change for the timing to work?', competitor: 'What do you value most in your current solution?', not_interested: "Of course. Can I ask what you're currently doing in this area?" }, closing_techniques: ['Shall we set up a 30-minute demo this week?', 'I can send over a proposal by Friday — does that work?'], follow_up_plan: 'Email within 24 hours, call back in 3 days if no response.', key_messages: ['Proven track record in UK engineering', 'Quick implementation, measurable ROI', 'Dedicated UK support team'] };
  } catch (err) {
    console.error(`❌ generateSalesScript: ${err.message}`);
    return { opening: 'Good morning, I am calling regarding our engineering services.', value_proposition: 'We help UK engineering firms improve efficiency and reduce costs.', discovery_questions: ['What challenges are you currently facing?', 'What solutions have you tried?', 'What would success look like for you?'], objection_handlers: {}, closing_techniques: ['Can we arrange a brief demonstration?'], follow_up_plan: 'Follow up within 48 hours.', key_messages: ['Expertise', 'Value', 'Results'] };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 🏭 ERP RECOMMENDATIONS
// ─────────────────────────────────────────────────────────────────────────────
export const generateERPRecommendation = async (module, data = {}, preferred = null) => {
  const safeData = safeObject(data);
  try {
    const response = await chatCompletion([
      { role: 'user', content:
          `ERP module recommendation for UK engineering company:\nModule: ${module}\nCurrent data: ${JSON.stringify(safeData).slice(0, 500)}\n\n` +
          `Return JSON: { "insights":["","",""], "alerts":[{"severity":"info|warning|critical","message":""}], "recommended_actions":["","",""], "kpi_summary":{"status":"green|amber|red","headline":"","metrics":["",""]}, "next_steps":["",""] }` },
    ], { temperature: 0.6, max_tokens: 600, task: 'erp', fnName: 'generateERPRecommendation' }, preferred);

    const result = repairJSON(response.choices[0].message.content);
    return result || { insights: [`${module} module operating normally`, 'No critical issues detected', 'Consider reviewing quarterly KPIs'], alerts: [], recommended_actions: ['Review module performance metrics', 'Check for pending approvals', 'Update forecasts'], kpi_summary: { status: 'green', headline: 'Module performing well', metrics: ['On track', 'Within budget'] }, next_steps: ['Schedule monthly review', 'Train new staff on module'] };
  } catch (err) {
    console.error(`❌ generateERPRecommendation: ${err.message}`);
    return { insights: [`${module} recommendation unavailable`], alerts: [], recommended_actions: ['Review manually'], kpi_summary: { status: 'amber', headline: 'Manual review required', metrics: [] }, next_steps: ['Check module manually'] };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 🤖 ML INSIGHT GENERATOR
// ─────────────────────────────────────────────────────────────────────────────
export const generateMLInsight = async (modelData = {}, datasetInfo = {}, preferred = null) => {
  const safeModel = safeObject(modelData); const safeDset = safeObject(datasetInfo);
  try {
    const data = await chatCompletion([
      { role: 'user', content:
          `ML model insight for UK engineering:\nModel: ${safeString(safeModel.name, 'ML Model')}\nType: ${safeString(safeModel.type, 'classification')}\nAccuracy: ${safeNumber(safeModel.accuracy, 85)}%\nDataset size: ${safeNumber(safeDset.rows, 10000)} rows\nFeatures: ${safeNumber(safeDset.features, 20)}\n\n` +
          `Return JSON: { "performance_assessment":"excellent|good|fair|poor", "improvement_suggestions":["","",""], "potential_issues":["",""], "deployment_readiness":0-100, "recommended_algorithms":["",""], "data_quality_score":0-100, "key_insights":["","",""] }` },
    ], { temperature: 0.5, max_tokens: 600, task: 'ml', fnName: 'generateMLInsight' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    return result || { performance_assessment: 'good', improvement_suggestions: ['Gather more training data', 'Feature engineering on key variables', 'Try ensemble methods'], potential_issues: ['Possible overfitting', 'Class imbalance'], deployment_readiness: 72, recommended_algorithms: ['Random Forest', 'XGBoost'], data_quality_score: 78, key_insights: ['Model shows strong performance on test set', 'Consider cross-validation with more folds', 'Monitor for data drift in production'] };
  } catch (err) {
    console.error(`❌ generateMLInsight: ${err.message}`);
    return { performance_assessment: 'good', improvement_suggestions: ['Collect more data', 'Tune hyperparameters', 'Review feature importance'], potential_issues: ['Requires manual validation'], deployment_readiness: 65, recommended_algorithms: ['Gradient Boosting', 'Neural Network'], data_quality_score: 70, key_insights: ['Please review model performance manually'] };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 📈 SCALE-UP STRATEGY GENERATOR
// ─────────────────────────────────────────────────────────────────────────────
export const generateScaleUpStrategy = async (businessData = {}, preferred = null) => {
  const safe = safeObject(businessData);
  try {
    const data = await chatCompletion([
      { role: 'user', content:
          `Scale-up strategy for UK engineering business:\nBusiness: ${safeString(safe.name, 'Engineering company')}\nRevenue: £${safeNumber(safe.revenue, 500000).toLocaleString()}\nStaff: ${safeNumber(safe.headcount, 10)}\nSector: ${safeString(safe.sector, 'Engineering')}\nChallenges: ${safeString(safe.challenges, 'Growth and scaling')}\nGoal: ${safeString(safe.goal, 'Double revenue in 3 years')}\n\n` +
          `Return JSON: { "growth_score":0-100, "recommended_strategies":["","","","",""], "quick_wins":["","",""], "12_month_milestones":["","",""], "funding_options":["","",""], "risks":["",""], "kpis":["","",""], "summary":"" }` },
    ], { temperature: 0.7, max_tokens: 900, task: 'business_plan', fnName: 'generateScaleUpStrategy' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    return result || getDefaultScaleUpStrategy(businessData);
  } catch (err) {
    console.error(`❌ generateScaleUpStrategy: ${err.message}`);
    return getDefaultScaleUpStrategy(businessData);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 📋 TENDER RESPONSE GENERATOR
// ─────────────────────────────────────────────────────────────────────────────
export const generateTenderResponse = async (opts = {}, preferred = null) => {
  const { tenderTitle = '', client = '', requirements = [], budget = '', deadline = '', context = '' } = opts;
  try {
    const data = await chatCompletion([
      { role: 'system', content: `You are a UK tender specialist. Write compelling, professional tender responses. Return ONLY valid JSON. No markdown.` },
      { role: 'user',   content:
          `Tender response framework:\nTender: ${tenderTitle}\nClient: ${client}\nBudget: ${budget || 'TBC'}\nDeadline: ${deadline}\nRequirements: ${requirements.join(', ')}\nContext: ${context}\n\n` +
          `Return JSON: { "executive_summary":"", "our_approach":"", "methodology":["","","",""], "team_overview":"", "relevant_experience":"", "quality_assurance":"", "pricing_narrative":"", "added_value":["","",""], "win_themes":["","",""] }` },
    ], { temperature: 0.7, max_tokens: 1200, task: 'letter', fnName: 'generateTenderResponse' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    return result || { executive_summary: `We are delighted to submit our response to the ${tenderTitle} tender.`, our_approach: 'Our approach combines technical excellence with proven delivery methodology.', methodology: ['Initial scoping and planning', 'Delivery and implementation', 'Testing and quality assurance', 'Handover and support'], team_overview: 'Our experienced team brings deep sector expertise.', relevant_experience: 'We have successfully delivered similar projects across the UK engineering sector.', quality_assurance: 'ISO 9001 certified with rigorous quality management processes.', pricing_narrative: 'Our pricing represents excellent value, balancing quality with cost-efficiency.', added_value: ['Dedicated account management', 'Ongoing maintenance and support', 'Knowledge transfer included'], win_themes: ['Proven track record', 'Local expertise', 'Value for money'] };
  } catch (err) {
    console.error(`❌ generateTenderResponse: ${err.message}`);
    return { executive_summary: `Response to ${tenderTitle}.`, our_approach: 'Comprehensive, professional approach.', methodology: ['Plan', 'Deliver', 'Review', 'Support'], team_overview: 'Experienced team.', relevant_experience: 'Extensive UK sector experience.', quality_assurance: 'Quality certified.', pricing_narrative: 'Competitive and transparent.', added_value: ['Support', 'Training', 'Warranty'], win_themes: ['Quality', 'Experience', 'Value'] };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 📊 PROJECT UPDATE GENERATOR
// ─────────────────────────────────────────────────────────────────────────────
export const generateProjectUpdate = async (projectData = {}, preferred = null) => {
  const safe = safeObject(projectData);
  try {
    const data = await chatCompletion([
      { role: 'user', content:
          `Project status update:\nProject: ${safeString(safe.name, 'Project')}\nProgress: ${safeNumber(safe.progress, 50)}%\nBudget spent: ${safeNumber(safe.budgetSpent, 60)}%\nDays remaining: ${safeNumber(safe.daysRemaining, 30)}\nStatus: ${safeString(safe.status, 'On Track')}\nRecent work: ${safeString(safe.recentWork, 'Development activities')}\nIssues: ${safeString(safe.issues, 'None major')}\n\n` +
          `Return JSON: { "headline_status":"On Track|At Risk|Off Track", "summary":"2-3 sentences", "achievements":["","",""], "risks_issues":["",""], "next_period_plan":["","",""], "stakeholder_message":"executive one-liner", "rag_status":"RED|AMBER|GREEN" }` },
    ], { temperature: 0.5, max_tokens: 600, task: 'report', fnName: 'generateProjectUpdate' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    return result || { headline_status: 'On Track', summary: `${safe.name || 'Project'} is progressing at ${safe.progress || 50}% completion. Activities are proceeding as planned with no major blockers.`, achievements: ['Milestone activities completed', 'Team delivering to plan', 'Budget within tolerance'], risks_issues: ['Monitor schedule closely', 'Continue stakeholder engagement'], next_period_plan: ['Continue planned activities', 'Complete next milestone', 'Stakeholder review'], stakeholder_message: `${safe.name || 'Project'} on track — ${safe.progress || 50}% complete.`, rag_status: 'GREEN' };
  } catch (err) {
    console.error(`❌ generateProjectUpdate: ${err.message}`);
    return { headline_status: 'On Track', summary: 'Project progressing well.', achievements: ['Good progress'], risks_issues: ['None identified'], next_period_plan: ['Continue delivery'], stakeholder_message: 'Project on track.', rag_status: 'GREEN' };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 📣 MARKETING CONTENT GENERATOR
// ─────────────────────────────────────────────────────────────────────────────
export const generateMarketingContent = async (opts = {}, preferred = null) => {
  const { type = 'social_post', product = '', audience = '', tone = 'professional', platform = '' } = opts;
  try {
    const data = await chatCompletion([
      { role: 'user', content:
          `Create UK B2B marketing ${type}:\nProduct/Service: ${product}\nTarget audience: ${audience}\nTone: ${tone}\nPlatform: ${platform || type}\n\n` +
          `Return JSON: { "headline":"", "body":"", "cta":"", "hashtags":["","",""], "subject_line":"", "alternative_versions":["",""] }` },
    ], { temperature: 0.8, max_tokens: 600, task: 'marketing', fnName: 'generateMarketingContent' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    return result || { headline: `Transform Your ${audience || 'Business'} with ${product}`, body: `Discover how ${product} is helping UK engineering firms achieve more. Our proven approach delivers results you can measure.`, cta: 'Learn More Today', hashtags: ['#Engineering', '#UKBusiness', '#Innovation'], subject_line: `${product}: Helping UK Engineering Firms Like Yours`, alternative_versions: [`Ready to elevate your ${audience || 'operations'}? ${product} delivers.`, `Join hundreds of UK firms benefiting from ${product}.`] };
  } catch (err) {
    console.error(`❌ generateMarketingContent: ${err.message}`);
    return { headline: `${product} — Built for UK Engineering`, body: `Professional engineering solutions for UK businesses.`, cta: 'Get in Touch', hashtags: ['#Engineering', '#Innovation'], subject_line: `${product} — UK Engineering Solutions`, alternative_versions: [] };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 💬 SENTIMENT ANALYSIS
// ─────────────────────────────────────────────────────────────────────────────
export const analyzeSentiment = async (text, context = '', preferred = null) => {
  if (!text?.trim()) return { sentiment: 'neutral', score: 50, confidence: 0, emotions: [], summary: '' };
  try {
    const data = await chatCompletion([
      { role: 'user', content:
          `Sentiment analysis${context ? ` (context: ${context})` : ''}:\n"${text.slice(0, 1000)}"\n\n` +
          `Return JSON: { "sentiment":"positive|negative|neutral|mixed", "score":0-100, "confidence":0-100, "emotions":["",""], "key_phrases":["",""], "summary":"one sentence" }` },
    ], { temperature: 0.2, max_tokens: 300, task: 'sentiment', fnName: 'analyzeSentiment' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    return result || { sentiment: 'neutral', score: 50, confidence: 60, emotions: [], summary: 'Sentiment analysis complete.', key_phrases: [] };
  } catch { return { sentiment: 'neutral', score: 50, confidence: 0, emotions: [], summary: 'Unable to analyse.', key_phrases: [] }; }
};

// ─────────────────────────────────────────────────────────────────────────────
// 🌍 CONTENT TRANSLATION  (UK professional tone)
// ─────────────────────────────────────────────────────────────────────────────
export const translateContent = async (text, targetLanguage = 'Spanish', preserveTone = 'professional', preferred = null) => {
  try {
    const data = await chatCompletion([
      { role: 'user', content: `Translate to ${targetLanguage} with ${preserveTone} tone:\n\n${text.slice(0, 2000)}\n\nReturn ONLY the translated text.` },
    ], { temperature: 0.3, max_tokens: 1500, task: 'translation', fnName: 'translateContent' }, preferred);
    return data.choices[0].message.content || text;
  } catch { return text; }
};

// ─────────────────────────────────────────────────────────────────────────────
// 📊 KEY INSIGHTS EXTRACTOR
// ─────────────────────────────────────────────────────────────────────────────
export const extractKeyInsights = async (text, domain = 'business', preferred = null) => {
  try {
    const data = await chatCompletion([
      { role: 'user', content:
          `Extract key insights from this ${domain} content:\n\n${text.slice(0, 2500)}\n\n` +
          `Return JSON: { "insights":["","","","",""], "action_items":["","",""], "key_metrics":["",""], "decisions_required":["",""], "follow_up_date":"", "priority":"low|medium|high" }` },
    ], { temperature: 0.4, max_tokens: 600, task: 'summary', fnName: 'extractKeyInsights' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    return result || { insights: ['Review original content for detailed insights'], action_items: ['Manual review recommended'], key_metrics: [], decisions_required: [], follow_up_date: '', priority: 'medium' };
  } catch { return { insights: ['Unable to extract insights automatically'], action_items: ['Manual review required'], key_metrics: [], decisions_required: [], follow_up_date: '', priority: 'medium' }; }
};

// ─────────────────────────────────────────────────────────────────────────────
// 📈 PERFORMANCE REVIEW GENERATOR  (HR)
// ─────────────────────────────────────────────────────────────────────────────
export const generatePerformanceReview = async (employeeData = {}, preferred = null) => {
  const safe = safeObject(employeeData);
  try {
    const data = await chatCompletion([
      { role: 'user', content:
          `UK employee performance review:\nName: ${safeString(safe.name, 'Employee')}\nRole: ${safeString(safe.role, 'Engineer')}\nPeriod: ${safeString(safe.period, 'Annual')}\nAchievements: ${safeString(safe.achievements, 'Good overall performance')}\nAreas to improve: ${safeString(safe.improvements, 'Communication')}\nRating: ${safeString(safe.rating, 'Meets Expectations')}\n\n` +
          `Return JSON: { "overall_summary":"", "strengths":["","",""], "development_areas":["",""], "objectives_next_period":["","",""], "training_recommendations":["",""], "career_development":"", "manager_comments":"" }` },
    ], { temperature: 0.6, max_tokens: 700, task: 'chat', fnName: 'generatePerformanceReview' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    return result || { overall_summary: `${safe.name || 'Employee'} has demonstrated solid performance during this review period and continues to contribute positively to the team.`, strengths: ['Technical competence', 'Reliable and punctual', 'Collaborative team member'], development_areas: ['Further develop leadership skills', 'Increase cross-team communication'], objectives_next_period: ['Complete professional development course', 'Lead a key project milestone', 'Improve KPI targets by 10%'], training_recommendations: ['Technical upskilling programme', 'Presentation skills workshop'], career_development: 'Continue to build towards senior role with targeted development plan.', manager_comments: `${safe.name || 'Employee'} is a valued member of the team. We look forward to continued development.` };
  } catch (err) {
    console.error(`❌ generatePerformanceReview: ${err.message}`);
    return { overall_summary: 'Performance review generated. Please customise with specific details.', strengths: ['Reliable', 'Competent', 'Team player'], development_areas: ['Leadership', 'Communication'], objectives_next_period: ['Meet quarterly targets', 'Complete training plan'], training_recommendations: ['Relevant technical training'], career_development: 'Discuss career aspirations at next 1-2-1.', manager_comments: 'Good overall performance.' };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 🗺️ ACTION PLAN GENERATOR  (generic, multi-screen use)
// ─────────────────────────────────────────────────────────────────────────────
export const generateActionPlan = async (goal, context = '', timeframe = '3 months', preferred = null) => {
  try {
    const data = await chatCompletion([
      { role: 'user', content:
          `Action plan for UK engineering context:\nGoal: ${goal}\nContext: ${context}\nTimeframe: ${timeframe}\n\n` +
          `Return JSON: { "goal_refined":"", "milestones":[{"week":1,"actions":["",""],"deliverable":""}], "resources_needed":["",""], "success_metrics":["","",""], "risks":["",""], "quick_wins":["","",""] }` },
    ], { temperature: 0.7, max_tokens: 800, task: 'chat', fnName: 'generateActionPlan' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    return result || { goal_refined: goal, milestones: [{ week: 1, actions: ['Initial planning and scoping', 'Stakeholder alignment'], deliverable: 'Approved project plan' }, { week: 4, actions: ['Core delivery activities', 'Progress review'], deliverable: 'Mid-point deliverable' }, { week: 8, actions: ['Finalise and review', 'Quality check'], deliverable: 'Final deliverable' }], resources_needed: ['Team allocation', 'Budget approval', 'Stakeholder time'], success_metrics: ['Goal achieved within timeframe', 'Budget maintained', 'Stakeholder satisfaction'], risks: ['Timeline slippage', 'Resource availability'], quick_wins: ['Low-hanging fruit first', 'Early stakeholder wins', 'Visible progress reporting'] };
  } catch (err) {
    console.error(`❌ generateActionPlan: ${err.message}`);
    return { goal_refined: goal, milestones: [], resources_needed: [], success_metrics: ['Goal achieved'], risks: [], quick_wins: [] };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 📰 REPORT SUMMARY GENERATOR
// ─────────────────────────────────────────────────────────────────────────────
export const generateReportSummary = async (reportData = {}, reportType = 'business', preferred = null) => {
  const safe = safeObject(reportData);
  try {
    const data = await chatCompletion([
      { role: 'user', content:
          `${reportType} report summary:\nData: ${JSON.stringify(safe).slice(0, 1500)}\n\n` +
          `Return JSON: { "executive_summary":"2-3 sentences", "headline_figures":["","","",""], "key_trends":["","",""], "recommendations":["","",""], "outlook":"positive|neutral|negative", "areas_of_concern":["",""] }` },
    ], { temperature: 0.5, max_tokens: 700, task: 'report', fnName: 'generateReportSummary' }, preferred);

    const result = repairJSON(data.choices[0].message.content);
    return result || { executive_summary: 'Report generated. Key metrics reviewed and analysed.', headline_figures: [], key_trends: ['Stable performance', 'Growth in key areas'], recommendations: ['Continue current strategy', 'Review underperforming areas', 'Increase investment in top performers'], outlook: 'positive', areas_of_concern: [] };
  } catch (err) {
    console.error(`❌ generateReportSummary: ${err.message}`);
    return { executive_summary: 'Report summary unavailable. Please review data manually.', headline_figures: [], key_trends: [], recommendations: ['Manual review required'], outlook: 'neutral', areas_of_concern: [] };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 💡 CAREER ADVICE  (for job-seeker/candidate screens)
// ─────────────────────────────────────────────────────────────────────────────
export const getCareerAdvice = async (query, userProfile = {}, preferred = null) => {
  const safe = safeObject(userProfile);
  try {
    const data = await chatCompletion([
      { role: 'system', content: `You are a UK career advisor specialising in engineering and technical roles. Give practical, actionable advice. UK context. 2-3 paragraphs max.` },
      { role: 'user',   content: `Career query: ${query}\nProfile: ${safeString(safe.role, 'Engineer')}, ${safeNumber(safe.experience, 3)} years experience, sector: ${safeString(safe.sector, 'Engineering')}, location: ${safeString(safe.location, 'UK')}` },
    ], { temperature: 0.7, max_tokens: 600, task: 'chat', fnName: 'getCareerAdvice' }, preferred);

    return data.choices[0].message.content || `Here is some career advice for UK engineering professionals: Focus on building technical depth alongside commercial awareness. Networking through institutions like IMechE and IET is valuable. Keep your CPD up to date and consider relevant certifications. The UK engineering sector values both technical excellence and project delivery skills.`;
  } catch { return 'Focus on continuous professional development, networking, and building both technical and commercial skills for UK engineering careers.'; }
};

// ─────────────────────────────────────────────────────────────────────────────
// 🔁 BATCH PROCESSING  (run multiple AI calls in parallel with rate limiting)
// ─────────────────────────────────────────────────────────────────────────────
export const batchProcess = async (items, processFn, options = {}) => {
  const { concurrency = 3, onProgress = null } = options;
  const results = [];
  for (let i = 0; i < items.length; i += concurrency) {
    const batch = items.slice(i, i + concurrency);
    const batchResults = await Promise.allSettled(batch.map(item => processFn(item)));
    batchResults.forEach((r, idx) => {
      results.push(r.status === 'fulfilled' ? r.value : { error: r.reason?.message, item: batch[idx] });
    });
    if (onProgress) onProgress(Math.min(i + concurrency, items.length), items.length);
    if (i + concurrency < items.length) await new Promise(r => setTimeout(r, 300)); // brief pause between batches
  }
  return results;
};

// ─────────────────────────────────────────────────────────────────────────────
// 🎭 FALLBACK / MOCK IMPLEMENTATIONS
// ─────────────────────────────────────────────────────────────────────────────
const getMockIdeas = (count, industry, reqId) => {
  const templates = [
    { title: `AI-Powered ${industry} Intelligence Platform`, description: `Advanced neural network predicting optimal outcomes with 94% accuracy using deep learning and real-time sensor fusion.`, type: 'ai_ml', impact: 'critical', estimated_roi: 425000, budget: 130000, tags: ['AI', 'Deep Learning', industry], confidence: 92, timeline: '4 months' },
    { title: `Smart ${industry} Automation Suite`, description: `End-to-end automation reducing manual work by 85% via intelligent workflow orchestration and RPA.`, type: 'automation', impact: 'high', estimated_roi: 380000, budget: 115000, tags: ['Automation', 'RPA', industry], confidence: 88, timeline: '3 months' },
    { title: `Predictive ${industry} Analytics Engine`, description: `Real-time analytics forecasting performance trends with 91% accuracy.`, type: 'analytics', impact: 'high', estimated_roi: 520000, budget: 155000, tags: ['Analytics', 'Predictive', industry], confidence: 85, timeline: '5 months' },
    { title: `${industry} Digital Twin Framework`, description: `Full-fidelity virtual asset models enabling real-time simulation and fault prediction.`, type: 'technology', impact: 'critical', estimated_roi: 680000, budget: 200000, tags: ['Digital Twin', 'IoT', industry], confidence: 79, timeline: '6 months' },
    { title: `${industry} Process Intelligence System`, description: `AI-driven process mining identifying inefficiencies and auto-generating optimisation blueprints.`, type: 'process', impact: 'medium', estimated_roi: 290000, budget: 90000, tags: ['Process Mining', industry], confidence: 82, timeline: '3 months' },
  ];
  return templates.slice(0, count).map((t, i) => ({ id: `mock-${reqId}-${i}`, ...t, stage: 'idea', industry, ml_generated: true, ai_mode: 'FALLBACK', provider: 'FALLBACK', model: 'mock', created_at: new Date().toISOString(), owner: { name: 'AI Generator (Offline)', id: 'mock' }, progress: 0, votes: 0, notes: [], bookmarked: false }));
};

const getMockBusinessPlan = (companyName, innovations, metrics) => ({
  executive_summary:     `${companyName || 'Your company'} is developing innovative AI solutions for the UK engineering sector. With ${safeArray(innovations).length} active projects and £${safeArray(innovations).reduce((s, i) => s + safeNumber(i?.estimated_roi), 0).toLocaleString()} combined ROI potential, we are positioned for significant market impact.`,
  market_analysis:       'The UK engineering innovation market reached £180B in 2024 (8.5% CAGR). AI adoption is accelerating, creating a £45B addressable opportunity. Neither legacy providers nor AI-native challengers match our full engineering sector depth.',
  products_services:     safeArray(innovations).length ? safeArray(innovations).map(i => `• ${safeString(i?.title, 'Innovation')}`).join('\n') : '• AI Engineering Platform\n• Predictive Analytics Suite\n• Automation Framework',
  marketing_strategy:    'B2B enterprise sales targeting UK engineering firms (500+ employees); IMechE/IET partnerships; content marketing; industry conference presence; AIM-listed benchmarks for investor positioning.',
  financial_projections: 'Year 1: £1.2M | Year 2: £3.5M | Year 3: £8.0M | Year 4: £15M | Year 5: £25M (60% EBITDA margin by Year 3)',
  risk_assessment:       'Key risks: talent acquisition, technology adoption rates, regulatory change. Mitigations: talent pipeline programme, phased rollout, compliance team.',
  team_structure:        'CEO, CTO, CDO + 3 domain experts, 5 engineers, 3 sales/BD, 2 operations.',
  funding_requirements:  500000,
  provider: 'fallback', generated_at: new Date().toISOString(),
});

const getMockLetterBody = (letterType, subject, recipientName) => {
  const bodies = {
    business:  `I am writing to you regarding ${subject || 'the matter under discussion'}.\n\nHaving carefully considered the requirements and having reviewed all relevant information, we are pleased to present our comprehensive approach. Our team brings considerable expertise in this area and is committed to delivering outstanding results that exceed expectations.\n\nWe believe our solution represents excellent value and aligns well with your stated objectives. The approach we have developed is both practical and innovative, drawing on our extensive experience in similar engagements across the UK engineering sector.\n\nWe would very much welcome the opportunity to discuss this further at your convenience. Please do not hesitate to contact us should you require any additional information.`,
    complaint: `I am writing to formally raise a concern regarding ${subject || 'the matter described below'}.\n\nDespite previous attempts to resolve this through normal channels, the situation remains unsatisfactory and requires your urgent attention. I believe this matter warrants formal consideration at a senior level.\n\nI would request a written response within 14 working days detailing the steps being taken to address this issue. I trust this can be resolved satisfactorily without the need for further escalation.`,
    inquiry:   `I am writing to make enquiries regarding ${subject || 'your services'}, and would be grateful for your assistance.\n\nWe are currently reviewing our options in this area and believe your organisation may be well-placed to assist. Could you please provide further information at your earliest convenience?\n\nWe would also welcome the opportunity to arrange a meeting or call to discuss our requirements in greater detail. I look forward to your response.`,
    tender:    `We are pleased to submit our tender in response to your invitation to tender for ${subject || 'the contract described'}.\n\nOur organisation has considerable experience delivering similar contracts and we are confident we can meet your requirements to the very highest standard. Our approach is both rigorous and innovative, and we believe it represents best value.\n\nWe would welcome the opportunity to present our proposal in more detail. We are available for interview or clarification at your convenience.`,
  };
  return bodies[letterType] || bodies.business;
};

const getMockEmailBody = (emailType, subject) => ({
  business: `Thank you for your email. I am writing to follow up regarding ${subject || 'the matter discussed'}.\n\nPlease find the requested information below. Should you require any further details, please do not hesitate to get in touch.\n\nWe look forward to your response.`,
  inquiry:  `Thank you for your enquiry. We would be delighted to provide further information about ${subject || 'our services'}.\n\nPlease review the attached information and let us know if you have any questions. We are happy to arrange a call or meeting at your convenience.`,
  follow_up:`I am following up on my previous email regarding ${subject || 'our earlier discussion'}. I wanted to ensure you received all the information you needed and check whether you have any questions.\n\nPlease let me know if there is anything further I can help with.`,
}[emailType] || `Thank you for your message. I look forward to discussing ${subject || 'this'} with you further.`);

const getDefaultAnalysis = (innovation) => ({
  success_probability: 78, market_fit_score: 72, technical_readiness: 65,
  strengths: ['Clear value proposition', 'Growing UK market demand', 'Experienced cross-functional team'],
  risks: ['Technical complexity', 'Competitive pressure', 'Enterprise adoption timeline'],
  recommendations: ['Launch focused 3-customer pilot', 'Gather quantitative feedback', 'Define ROI measurement framework'],
  next_steps: ['Complete market sizing', 'Build and test MVP', 'Identify 3 pilot customers'],
  time_to_market: '4-6 months', competitive_advantage: 'Domain-specific AI for UK engineering sector',
  summary: 'Promising innovation with solid fundamentals — focus on de-risking technical delivery.',
  provider: 'fallback', analysed_at: new Date().toISOString(),
});

const getDefaultCompetitorAnalysis = () => ({
  competitors: [
    { name: 'Legacy Platform A', description: 'Incumbent with 40% market share', market_share: '40%', weakness: 'Slow innovation, high TCO' },
    { name: 'AI Scale-up B',     description: 'AI-native challenger, mid-market', market_share: '15%', weakness: 'Limited UK regulatory expertise' },
    { name: 'Enterprise Suite C',description: 'Large SI with bundled offering',   market_share: '20%', weakness: 'Low customisation' },
  ],
  our_advantages: ['AI-first architecture with UK engineering domain depth', 'Faster time-to-value (8 weeks vs 6 months)', 'Transparent explainable AI'],
  market_gap: 'No solution combines real-time AI with deep UK engineering compliance and domain knowledge.',
  differentiation_score: 74, threat_level: 'medium', market_size: '£45B TAM', growth_rate: '8.5% CAGR',
});

const getDefaultROIProjection = (innovation, years = 5) => {
  const invest = safeNumber(safeObject(innovation).budget, 80000);
  const target = safeNumber(safeObject(innovation).estimated_roi, 250000);
  const annual = target / years; let cum = -invest;
  const yearly = Array.from({ length: years }, (_, i) => {
    const rev = Math.round(annual * (1 + i * 0.15)); const costs = Math.round(invest * 0.2 * (1 + i * 0.05));
    const profit = rev - costs; cum += profit;
    return { year: i + 1, revenue: rev, costs, profit, cumulative_roi: Math.round(cum) };
  });
  return { yearly, payback_months: Math.ceil(invest / (annual / 12)), irr_percent: 28.5, npv: Math.round(cum * 0.85), assumptions: ['8% annual revenue growth', '20% overhead on initial investment', 'No major market disruption'] };
};

const getDefaultPortfolioSummary = (innovations) => {
  const safe = safeArray(innovations); const totalROI = safe.reduce((s, i) => s + safeNumber(i?.estimated_roi), 0);
  const score = Math.min(95, 40 + safe.length * 4 + Math.round(totalROI / 60000));
  return { headline: `${safe.length} innovations · £${(totalROI / 1000).toFixed(0)}k total estimated ROI`, portfolio_score: score, key_strengths: ['Active AI adoption', 'Diverse innovation types'], urgent_actions: ['Review stalled projects', 'Prioritise top-3 ROI items'], opportunities: ['Scale pilot innovations', 'Cross-industry applications'], outlook: score >= 65 ? 'positive' : score >= 45 ? 'neutral' : 'concerning', recommended_priority: safe[0]?.title || 'Review portfolio' };
};

const getDefaultRiskAssessment = (projectName) => ({
  risks: [
    { id: 1, category: 'Technical',     title: 'Technical complexity',       description: 'Project may encounter unforeseen technical challenges', likelihood: 'Medium', impact: 'High',   risk_score: 12, mitigation: 'Regular technical reviews and prototype testing', owner: 'Technical Lead', status: 'Open' },
    { id: 2, category: 'Financial',     title: 'Budget overrun',             description: 'Costs may exceed original estimates',                  likelihood: 'Medium', impact: 'Medium', risk_score: 9,  mitigation: 'Monthly budget reviews with contingency reserve',  owner: 'PM',           status: 'Open' },
    { id: 3, category: 'Operational',   title: 'Resource availability',      description: 'Key resources may not be available when required',     likelihood: 'Low',    impact: 'High',   risk_score: 8,  mitigation: 'Resource plan agreed and booked in advance',        owner: 'PM',           status: 'Open' },
    { id: 4, category: 'Legal',         title: 'Regulatory compliance',      description: 'Changes in UK regulation may affect delivery',          likelihood: 'Low',    impact: 'Medium', risk_score: 4,  mitigation: 'Legal review at key milestones',                    owner: 'Legal',        status: 'Open' },
  ],
  overall_risk_level: 'Medium', key_concerns: ['Technical delivery', 'Budget management', 'Stakeholder alignment'],
  regulatory_considerations: ['UK GDPR compliance', 'Health & Safety at Work Act', 'ISO 9001 quality standards'],
});

const getDefaultScaleUpStrategy = (businessData) => {
  const safe = safeObject(businessData);
  return {
    growth_score: 68,
    recommended_strategies: ['Expand into adjacent UK market segments', 'Build strategic partnerships with tier-1 engineering firms', 'Invest in digital marketing and thought leadership', 'Develop a scalable SaaS/recurring revenue model', 'Target public sector procurement frameworks'],
    quick_wins: ['List on government procurement portals', 'Apply for Innovate UK grant funding', 'Launch referral programme with existing clients'],
    '12_month_milestones': ['Secure 3 new enterprise clients', 'Launch version 2.0 of core offering', 'Achieve £1M ARR target'],
    funding_options: ['Innovate UK grant programmes', 'SEIS/EIS angel investment', 'British Business Bank scale-up loan', 'Venture debt facility', 'AIM market listing (3-5 year horizon)'],
    risks: ['Scaling customer support quality', 'Hiring senior talent in competitive market'],
    kpis: ['Monthly recurring revenue (MRR)', 'Customer acquisition cost (CAC)', 'Net revenue retention (NRR)', 'Gross margin %'],
    summary: `${safe.name || 'Company'} shows strong scale-up potential. Focus on repeatable sales motion and operational efficiency before aggressive expansion.`,
  };
};

const getDefaultResponse = (query, innovations) => {
  const safe = safeArray(innovations); const totalROI = safe.reduce((s, i) => s + safeNumber(i?.estimated_roi), 0);
  const opts = [
    `Your portfolio of ${safe.length} innovations has £${totalROI.toLocaleString()} combined ROI potential. Focus on advancing pilot-stage projects to scale.`,
    `"${safeString(safe[0]?.title, 'Your top innovation')}" shows £${safeNumber(safe[0]?.estimated_roi).toLocaleString()} ROI potential. Consider increasing budget to accelerate delivery.`,
    `Prioritise high-impact, low-progress items — these offer the best near-term value capture.`,
    `Average portfolio health is ${safe.length ? Math.round(safe.reduce((s, i) => s + safeNumber(i?.health), 0) / safe.length) : 75}%. Review lowest-scoring projects this sprint.`,
  ];
  return opts[Math.floor(Math.random() * opts.length)];
};